import React, { useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { Common } from "@src/styles/Common";

import MgInput from "../../components/input/MgInput";

import { CHECK_TEXT } from "../../util/expertService";

import { useSignupExpertStore } from "@src/store/signupExpertStore";

import CheckIcon from "../../assets/Signup/CheckIcon";
import Remove from "../../assets/Signup/remove";
import { CounselingCategory } from "@src/api/expert/expertApi";
import { uploadExpertProfileImage } from "@src/api/expert/expertApi";
import { useCertificateStore } from "../../store/ExpertInfoStore";
import { COUNCELING_CATEGORIES_ORDER } from "@src/pages/signupExpert";
import { transferToNative, WebAppBridge, getDeviceType } from "@src/lib/WebAppBridge";

type ServiceToggleButtonProps = {
    checked: boolean;
    label: string;
    onToggle: () => void;
};

// 원하는 전문가 선택 탭
const ServiceToggleButton: React.FC<ServiceToggleButtonProps> = ({ checked, label, onToggle }) => (
    <ListButton type="button" onClick={onToggle}>
        <CheckIcon isChecked={checked} />
        <ListText>{label}</ListText>
    </ListButton>
);

export type PreviewItem = { id: string; url: string };

type DelegationUploadBoxProps = {
    enabled: boolean;
};

const DelegationUploadBox: React.FC<DelegationUploadBoxProps> = ({ enabled }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const { auctionAgentCertFileUrl, setAuctionAgentCertFileUrl } = useSignupExpertStore((state) => ({
        auctionAgentCertFileUrl: state.form.auctionAgentCertFileUrl,
        setAuctionAgentCertFileUrl: (url: string) =>
            state.setPartialState({
                form: { ...state.form, auctionAgentCertFileUrl: url },
            }),
    }));

    useEffect(() => {
        if (!enabled) return;
        if (getDeviceType() !== "android") return;

        WebAppBridge.onGetCertificateImage = (imageUrl: string) => {
            setAuctionAgentCertFileUrl(imageUrl);
        };

        return () => {
            WebAppBridge.onGetCertificateImage = undefined as any;
        };
    }, [enabled, setAuctionAgentCertFileUrl]);

    if (!enabled) return null;

    const handleClickUpload = () => {
        if (getDeviceType() === "android") {
            transferToNative("", "onClickCertificate");
            return;
        }
        inputRef.current?.click();
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const url = await uploadExpertProfileImage(file);
        setAuctionAgentCertFileUrl(url);
    };
    
    return (
        <>
            <HelperText>매수신청대리인만이 경매대행 업무가 가능해요.</HelperText>

            <DelegationWrap>
                <Line />
                <Upload>
                    <SectionTitle>매수신청대리인 등록증</SectionTitle>
                    <Button type="button" onClick={handleClickUpload}>
                        사진 업로드
                    </Button>

                    <HiddenFileInput ref={inputRef} type="file" accept="image/*" onChange={handleChange} />
                </Upload>

                {auctionAgentCertFileUrl && (
                    <PreviewContainer>
                        <Item>
                            <img src={auctionAgentCertFileUrl} alt="업로드된 미리보기" />
                            <RemoveBtn type="button" onClick={() => setAuctionAgentCertFileUrl("")}>
                                <Remove />
                            </RemoveBtn>
                        </Item>
                    </PreviewContainer>
                )}
            </DelegationWrap>
        </>
    );
};

const ExpertSelect = () => {
    const navigate = useNavigate();
    const { setPartialState, form } = useSignupExpertStore();

    const toggleCategory = (category: CounselingCategory) => {
        if (form.counselingCategories.includes(category)) {
            const updatedCareer = { ...form.career } as Record<CounselingCategory, number>;
            delete updatedCareer[category];

            if (category === "매수신청대리인") {
                const updatedForm = { ...form };
                delete updatedForm.auctionAgentCertFileUrl;
                if (!form.counselingCategories.includes("중개")) {
                    delete updatedForm.officeInfo;
                }

                setPartialState({
                    form: {
                        ...updatedForm,
                        counselingCategories: form.counselingCategories.filter((c) => c !== category),
                        career: updatedCareer,
                    },
                });
                return;
            }

            if (category === "중개") {
                const updatedForm = { ...form };
                if (!form.counselingCategories.includes("매수신청대리인")) {
                    delete updatedForm.officeInfo;
                }
                setPartialState({
                    form: {
                        ...updatedForm,
                        counselingCategories: form.counselingCategories.filter((c) => c !== category),
                        career: updatedCareer,
                    },
                });
            }

            setPartialState({
                form: {
                    ...form,
                    counselingCategories: form.counselingCategories.filter((c) => c !== category),
                    career: updatedCareer,
                },
            });
        } else {
            setPartialState({
                form: {
                    ...form,
                    counselingCategories: [...form.counselingCategories, category],
                    career: { ...form.career, [category]: 0 } as Record<CounselingCategory, number>,
                },
            });
        }
    };

    const delegationOn = form.counselingCategories.includes("매수신청대리인");
    const brokerageOn = form.counselingCategories.includes("중개");
    const officeSummary = form.officeInfo
        ? `${form.officeInfo.name}${form.officeInfo.address ? ` (${form.officeInfo.address})` : ""}`
        : "";

    const goOfficeSearch = useCallback(() => {
        navigate("/signup/officeSearch");
    }, [navigate]);

    return (
        <Container>
            <Tabs role="tablist" aria-label="서비스 선택 탭">
                {COUNCELING_CATEGORIES_ORDER.map((category) => {
                    const active = form.counselingCategories.includes(category);
                    return (
                        <Tab
                            key={category}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            data-active={active}
                            aria-pressed={active}
                            onClick={() => toggleCategory(category)}
                        >
                            {category === "매수신청대리인" ? "경매대행" : category}
                        </Tab>
                    );
                })}
            </Tabs>
            <AlwaysList>
                <Row>
                    <ServiceToggleButton
                        checked={form.counselingCategories.includes("중개")}
                        label={CHECK_TEXT["중개"]}
                        onToggle={() => toggleCategory("중개")}
                    />
                </Row>

                <Row>
                    <ServiceToggleButton
                        checked={form.counselingCategories.includes("매물컨설팅")}
                        label={CHECK_TEXT["매물컨설팅"]}
                        onToggle={() => toggleCategory("매물컨설팅")}
                    />
                </Row>

                <Row>
                    <ServiceToggleButton
                        checked={delegationOn}
                        label={CHECK_TEXT["매수신청대리인"]}
                        onToggle={() => toggleCategory("매수신청대리인")}
                    />

                    <DelegationUploadBox enabled={delegationOn} />
                </Row>

                <Row>
                    <ServiceToggleButton
                        checked={form.counselingCategories.includes("임장동행")}
                        label={CHECK_TEXT["임장동행"]}
                        onToggle={() => toggleCategory("임장동행")}
                    />

                    {(delegationOn || brokerageOn) && (
                        <OfficeInputWrapper onClick={goOfficeSearch}>
                            <InputHeader>
                                <span className="star">*</span>
                                <span className="title">중개사무소 조회</span>
                            </InputHeader>
                            <MgInput
                                placeholder="조회하여 입력이 가능합니다."
                                value={officeSummary}
                                readOnly
                                noBorder
                                bgColor="#FBF8F8"
                                style={{
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    width: "100%",
                                    display: "block",
                                }}
                            />
                        </OfficeInputWrapper>
                    )}
                </Row>
            </AlwaysList>
        </Container>
    );
};

export default ExpertSelect;

const Container = styled.div``;

const AlwaysList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: column;
`;

const ListButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    text-align: left;
    background: transparent;

    &:focus-visible {
        outline: 2px solid #2d73ff;
        border-radius: 8px;
    }
`;

const ListText = styled.span`
    font-size: 16px;
    font-weight: 700;
    line-height: 140%;
    color: #140c2c;
`;

const InputHeader = styled.div`
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;

    .star {
        color: ${Common.colors.notiColor};
    }
    .title {
        color: ${Common.colors.normalTextColor};
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
    }
`;

const HelperText = styled.p`
    margin: 0 0 0 34px;
    font-size: 14px;
    color: #a3a3a3;
    line-height: 140%;
`;

const DelegationWrap = styled.div`
    margin: 0 0 0 34px;
`;

const Upload = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const Line = styled.div`
    margin: 8px 0;
    width: 100%;
    height: 1px;
    background-color: #f2f3f6;
`;

const SectionTitle = styled.h3`
    font-size: 14px;
    font-weight: 400;
    color: #0c002c;
    line-height: 140%;
    margin: 0;
`;

const OfficeInputWrapper = styled.div`
    width: 100%;
    cursor: pointer;
    & input,
    & textarea {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`;

const Button = styled.button`
    padding: 3px 10px;
    background: #2d73ff;
    color: #fff;
    border: none;
    cursor: pointer;
    border-radius: 8px;
    font-weight: 400;
    font-size: 14px;

    &:focus-visible {
        outline: 2px solid #2d73ff;
        border-radius: 6px;
    }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const PreviewContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: 12px;
    padding-top: 12px;
`;

const Item = styled.div`
    position: relative;
    border: 1px solid #505050;
    border-radius: 8px;
    aspect-ratio: 1 / 1;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        border-radius: 8px;
    }
`;

const RemoveBtn = styled.button`
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(35%, -35%);
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    cursor: pointer;
    background: transparent;

    svg {
        width: 24px;
        height: 24px;
        display: block;
    }
`;

const Tabs = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px;
    margin-bottom: 10px;
`;

const Tab = styled.button`
    height: 44px;
    border-radius: 1000px;
    border: 0;
    font-size: 14px;
    cursor: pointer;
    font-weight: 400;
    letter-spacing: -0.2px;

    ${({ theme }) => css`
        &[data-active="true"] {
            background: #2275ee;
            color: #fff;
        }
        &[data-active="false"] {
            background: #fbf8f8;
            color: #c9c9c9;
        }
    `}

    &:focus-visible {
        outline: 2px solid #2d73ff;
    }

    @media screen and (max-width: 380px) {
        font-size: 12px;
    }

    @media screen and (min-width: 768px) {
        font-size: 12px;
    }
`;
